import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UnsubscribeOpenPositionModal } from '@pages/Subscriptions/modals/UnsubscribeOpenPosition.modal'

import { useModal } from '@context/ModalsContext'

import { Link } from '@ui/components/Link'
import { PageSection } from '@ui/components/PageSection'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { isZero } from '@utils/validations'

import { SubscriptionDetail } from '@model/SubscriptionTypes'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useFetchOne } from '@hooks/useFetch'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { InformationModal as SharedInformationModal } from '@shared/modals/Information.modal'

import { Cart } from '../Cart'
import { SubscriptionsTemplate } from '../SubscriptionsTemplate'
import { useAvailableToWithdraw, useCartItemsData } from '../hooks/useCartItemsData'
import { PaymentDoneModal } from '../modals/PaymentDone.modal'
import { SubscriptionContent } from './SubscriptionContent'
import { getSubscribedBundles } from './subscriptionUtils'

const useSubscriptionFetch = (counter: number) => {
  const clientApiClient = useApiClient(ClientApiClient)

  const subscriptionBundlesCallback = useCallback(() => {
    return clientApiClient.getSubscriptionBundles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter])

  const { data: subscriptionBundles = [], isLoading: subscriptionBundlesLoading } = useFetchOne(
    subscriptionBundlesCallback
  )

  const subscriptionGroupCallback = useCallback(() => {
    return clientApiClient.getSubscriptionGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter])

  const { data: subscriptionGroups = [], isLoading: subscriptionGroupsLoading } =
    useFetchOne(subscriptionGroupCallback)

  return {
    subscriptionBundles,
    subscriptionGroups,
    subscriptionBundlesLoading,
    subscriptionGroupsLoading,
  }
}

export const MarketDataPage: React.FC = () => {
  useScrollToTop()

  const { t } = useTranslation()
  const clientApiClient = useApiClient(ClientApiClient)

  const [counter, setCounter] = useState(0)
  const isMobile = useWindowResize()

  const subscriptionFetchProps = useSubscriptionFetch(counter)
  const cartItemsData = useCartItemsData()
  const { isTradingAccountsLoading, availableToWithdraw } = useAvailableToWithdraw()

  const { subscriptionGroups, subscriptionGroupsLoading } = subscriptionFetchProps
  const { subscriptionBundles, subscriptionBundlesLoading } = subscriptionFetchProps
  const subscribedBundles = getSubscribedBundles(subscriptionBundles)

  const paymentDoneModal = useModal(PaymentDoneModal)

  const alreadySubscribedInfo = useModal(SharedInformationModal, {
    title: t('Subscriptions.Already Subscribed'),
    secondaryLabel: t('Got it!'),
  })
  const mutuallyExclusiveInfo = useModal(SharedInformationModal, {
    title: t('Subscriptions.Already Subscribed'),
    secondaryLabel: t('Got it!'),
  })
  const newExclusionInfo = useModal(SharedInformationModal, {
    title: t('Subscriptions.Already Subscribed'),
    secondaryLabel: t('Got it!'),
    description: () => (
      <p className='text'>
        {t(
          'Subscriptions.The selected subscription combination is not allowed due to mutual exclusivity'
        )}
      </p>
    ),
  })

  const handleOnUpdateSubscription = async (
    data: UpdateSubscriptionRequest,
    selectedItem?: SubscriptionDetail
  ) => {
    try {
      await clientApiClient.updateSubscriptionRequest(data)
      cartItemsData.forceRefreshCartItems()
      setCounter((prev) => prev + 1)
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'trading_account_has_open_positions'
      ) {
        return openPositionModal.open({ subscriptionRequest: selectedItem })
      }

      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'subscription_detail_is_excluded'
      ) {
        const props = error.response.response?.data?.properties
        return mutuallyExclusiveInfo.open({
          description: () => (
            <p className='text'>
              {t('Subscriptions.Bundle is mutually exclusive', {
                parent: props?.ExcludedBySubscriptionDetail?.Name,
                child: props?.SubscriptionDetail?.Name,
              })}
            </p>
          ),
        })
      }

      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'subscription_detail_is_excluded_by_bundle'
      ) {
        const props = error.response.response?.data?.properties
        return alreadySubscribedInfo.open({
          description: () => (
            <p className='text'>
              {t('Subscriptions.Bundle is is already subscribed', {
                parent: props?.BundleParentSubscriptionDetail?.Name,
                child: props?.BundleChildSubscriptionDetail?.Name,
              })}
            </p>
          ),
        })
      }

      if (
        error instanceof ResponseError &&
        (error.response.response?.data.code === 'subscription_detail_is_excluded_by_cart_bundle' ||
          error.response.response?.data.code === 'subscription_detail_is_excluded_by_cart' ||
          error.response.response?.data.code ===
            'subscription_detail_is_excluded_by_pending_bundle' ||
          error.response.response?.data.code === 'subscription_detail_is_excluded_by_pending')
      ) {
        return newExclusionInfo.open()
      }
    }
  }

  const handleOnClickUnsubscribeWithOpenPosition = (
    subscriptionRequest?: SubscriptionDetail
  ): void => {
    handleOnUpdateSubscription(
      {
        subscriptions: [
          {
            subscriptionDetailId: subscriptionRequest?.id,
            subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
          },
        ],
        overrideOpenPositionsForCancel: true,
        subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
      },
      subscriptionRequest
    )

    openPositionModal.close()
  }

  const handleOnClickPay = async () => {
    const noneActiveSubscriptionsMapped = cartItemsData.noneActiveSubscriptions?.map((item) => ({
      subscriptionDetailId: item.subscriptionDetail?.id || '',
      subscriptionRequestTypeId: item.subscriptionRequestType.id,
    }))

    await clientApiClient.updateSubscriptionRequest({
      subscriptions: noneActiveSubscriptionsMapped,
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
    })

    setCounter((prev) => prev + 1)
    cartItemsData.forceRefreshCartItems()
    paymentDoneModal.open()
  }

  const handleOnRemoveCartItem = async (id: string) => {
    await clientApiClient.deleteSubscriptionCartItem(id)
    setCounter((prev) => prev + 1)
    cartItemsData.forceRefreshCartItems()
  }

  const handleOnAddCartItem = async (
    subscriptionDetailId: string,
    subscriptionRequestTypeId: SubscriptionRequestType
  ) => {
    try {
      await clientApiClient.addSubscriptionItemToCart({
        subscriptionDetailId,
        subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
        subscriptionRequestTypeId,
      })

      cartItemsData.forceRefreshCartItems()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'subscription_detail_is_excluded'
      ) {
        const props = error.response.response?.data?.properties
        return mutuallyExclusiveInfo.open({
          description: () => (
            <p className='text'>
              {t('Subscriptions.Bundle is mutually exclusive', {
                parent: props?.ExcludedBySubscriptionDetail?.Name,
                child: props?.SubscriptionDetail?.Name,
              })}
            </p>
          ),
        })
      }

      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'subscription_detail_is_excluded_by_bundle'
      ) {
        const props = error.response.response?.data?.properties
        return alreadySubscribedInfo.open({
          description: () => (
            <p className='text'>
              {t('Subscriptions.Bundle is is already subscribed', {
                parent: props?.BundleParentSubscriptionDetail?.Name,
                child: props?.BundleChildSubscriptionDetail?.Name,
              })}
            </p>
          ),
        })
      }

      if (
        error instanceof ResponseError &&
        (error.response.response?.data.code === 'subscription_detail_is_excluded_by_cart_bundle' ||
          error.response.response?.data.code === 'subscription_detail_is_excluded_by_cart' ||
          error.response.response?.data.code ===
            'subscription_detail_is_excluded_by_pending_bundle' ||
          error.response.response?.data.code === 'subscription_detail_is_excluded_by_pending')
      ) {
        return newExclusionInfo.open()
      }
    }
  }

  const isCartEmpty =
    isZero(cartItemsData.noneActiveSubscriptions?.length) &&
    isZero(cartItemsData.activeSubscriptions?.length)

  const isCartItemsLoading =
    isTradingAccountsLoading ||
    cartItemsData.isNoneActiveSubscriptionsLoading ||
    cartItemsData.isActiveSubscriptionsLoading ||
    cartItemsData.isPendingUnsubscriptionsLoading

  const openPositionModal = useModal(UnsubscribeOpenPositionModal, {
    onConfirm: handleOnClickUnsubscribeWithOpenPosition,
  })

  return (
    <SubscriptionsTemplate
      subscriptionsHeader={<MarketDataPageHeader />}
      subscriptionsTable={
        <div className='mt-5'>
          <SubscriptionContent
            subscriptionBundlesLoading={subscriptionBundlesLoading}
            subscriptionGroupsLoading={subscriptionGroupsLoading}
            scrollToCart={cartItemsData.scrollToCart}
            subscribedBundles={subscribedBundles}
            subscriptionGroups={subscriptionGroups}
            subscriptionBundles={subscriptionBundles}
            onUpdateSubscription={handleOnUpdateSubscription}
            onAddCartItem={handleOnAddCartItem}
            nonActiveSubscriptions={cartItemsData.noneActiveSubscriptions}
          />
        </div>
      }
      hasSubscriptions={cartItemsData.hasSubscriptions}
      isCartItemsLoading={isCartItemsLoading}
      isCartEmpty={isMobile ? isCartEmpty : isCartEmpty && !isCartItemsLoading}
      sidebar={[
        <Cart
          isExpanded={cartItemsData.isExpanded}
          setIsExpanded={cartItemsData.setIsExpanded}
          cartItemsIsLoading={isCartItemsLoading}
          handleOnUpdateSubscription={handleOnUpdateSubscription}
          handleOnClickPay={handleOnClickPay}
          onRemoveCartItem={handleOnRemoveCartItem}
          hasSubscriptions={cartItemsData.hasSubscriptions}
          activeSubscriptions={cartItemsData.activeSubscriptions}
          hasPendingUnsubscriptionRequest={cartItemsData.pendingUnsubscriptions?.itemsCount > 0}
          pendingUnsubscriptionsData={cartItemsData.pendingUnsubscriptionsData}
          noneActiveSubscriptions={cartItemsData.noneActiveSubscriptions}
          availableToWithdraw={availableToWithdraw}
          upgradeOrDowngradeSubscriptionId={cartItemsData.upgradeOrDowngradeSubscriptionId}
        />,
      ]}
    />
  )
}

const MarketDataPageHeader = () => {
  const { t } = useTranslation()

  const title = t('Market Data.How to use Market data')
  const path = '/dashboard/subscriptions/market-data/why-market-data'

  return (
    <PageSection
      title={t('Market Data.title')}
      renderMessage={() => <Link to={path}>{title}</Link>}
    />
  )
}
